import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, Clock, AlertTriangle, Lightbulb, ListTodo, Loader2, ChevronDown, ChevronRight } from 'lucide-react';
import { clsx } from 'clsx';
import { api } from '../lib/api';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { EmptyState } from '../components/ui/EmptyState';
import { ConfirmDialog } from '../components/ui/ConfirmDialog';
import { useToast } from '../components/ui/Toast';
import { EvidenceDetail } from '../components/recommendations/EvidenceDetail';
import { usePermissions } from '../hooks/usePermissions';

interface Recommendation {
  id: string;
  agentType: string;
  category: string;
  priority: string;
  title: string;
  summary: string;
  confidence: number;
  status: string;
  requiresProfessionalReview: boolean;
  evidenceIds?: string[];
  agentRunId?: string;
  createdAt: string;
}

export function RecommendationsPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const toast = useToast();
  const { can } = usePermissions();
  const canWrite = can('recommendations:write');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [rejectTarget, setRejectTarget] = useState<string | null>(null);
  const [expandedEvidence, setExpandedEvidence] = useState<Set<string>>(new Set());
  const [staleAcceptTarget, setStaleAcceptTarget] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['recommendations'],
    queryFn: () => api.getRecommendations(),
  });

  // Fetch profile completeness for staleness detection
  const { data: completeness } = useQuery({
    queryKey: ['profileCompleteness'],
    queryFn: () => api.getProfileCompleteness(),
  });

  const updateStatusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      api.updateRecommendationStatus(id, status),
    onSuccess: (_data, { status }) => {
      queryClient.invalidateQueries({ queryKey: ['recommendations'] });
      queryClient.invalidateQueries({ queryKey: ['nextActions'] });
      const labels: Record<string, string> = { accepted: 'Recommendation accepted', rejected: 'Recommendation rejected', viewed: 'Marked for later review' };
      toast.success(labels[status] ?? 'Status updated');
    },
    onError: () => {
      toast.error('Failed to update recommendation');
    },
  });

  const createPlanMutation = useMutation({
    mutationFn: (data: { title: string; goal?: string; recommendationIds: string[] }) =>
      api.createPlan(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['plans'] });
      queryClient.invalidateQueries({ queryKey: ['recommendations'] });
      setSelectedIds(new Set());
      toast.success('Action plan created successfully');
      navigate('/plans');
    },
    onError: () => {
      toast.error('Failed to create action plan');
    },
  });

  const recommendations: Recommendation[] = data?.data ?? [];
  const acceptedRecommendations = recommendations.filter((r) => r.status === 'accepted');

  // Staleness: section is 'verified' or 'extracted' (not 'analyzed') means data changed since last run
  const taxSectionStatus = completeness?.sections?.tax?.status;
  const isTaxStale = taxSectionStatus === 'verified' || taxSectionStatus === 'extracted';
  const estateSectionStatus = completeness?.sections?.estate?.status;
  const isEstateStale = estateSectionStatus === 'verified' || estateSectionStatus === 'extracted';
  const insuranceSectionStatus = completeness?.sections?.insurance?.status;
  const isInsuranceStale = insuranceSectionStatus === 'verified' || insuranceSectionStatus === 'extracted';
  const isAnalysisStale = isTaxStale || isEstateStale || isInsuranceStale;

  const toggleSelection = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) { next.delete(id); } else { next.add(id); }
      return next;
    });
  };

  const selectAllAccepted = () => {
    setSelectedIds(new Set(acceptedRecommendations.map((r) => r.id)));
  };

  const clearSelection = () => {
    setSelectedIds(new Set());
  };

  const toggleEvidence = (id: string) => {
    setExpandedEvidence((prev) => {
      const next = new Set(prev);
      if (next.has(id)) { next.delete(id); } else { next.add(id); }
      return next;
    });
  };

  const handleAccept = (rec: Recommendation) => {
    if (!canWrite) return;
    const recIsStale = (rec.agentType === 'tax' && isTaxStale) || (rec.agentType === 'estate' && isEstateStale) || (rec.agentType === 'insurance' && isInsuranceStale);
    if (recIsStale) {
      setStaleAcceptTarget(rec.id);
    } else {
      updateStatusMutation.mutate({ id: rec.id, status: 'accepted' });
    }
  };

  const handleCreatePlan = () => {
    if (!canWrite || selectedIds.size === 0) return;
    const selectedRecs = recommendations.filter((r) => selectedIds.has(r.id));
    const title =
      selectedRecs.length === 1
        ? selectedRecs[0].title
        : `Plan with ${selectedRecs.length} recommendations`;
    const goal = selectedRecs.map((r) => r.summary).join('; ');

    createPlanMutation.mutate({
      title,
      goal: goal.slice(0, 500),
      recommendationIds: Array.from(selectedIds),
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Recommendations</h1>
          <p className="text-gray-600">Review AI-generated insights and accept the ones that fit your strategy</p>
        </div>

        {/* Create Plan Section */}
        {acceptedRecommendations.length > 0 && (
          <div className="flex items-center space-x-2">
            {selectedIds.size > 0 ? (
              <>
                <span className="text-sm text-gray-600">{selectedIds.size} selected</span>
                <button
                  onClick={clearSelection}
                  className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800"
                >
                  Clear
                </button>
                <button
                  onClick={handleCreatePlan}
                  disabled={createPlanMutation.isPending}
                  className="flex items-center px-4 py-2 bg-legacy-600 text-white rounded-md hover:bg-legacy-700 transition-colors disabled:opacity-50"
                >
                  {createPlanMutation.isPending ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <ListTodo className="h-4 w-4 mr-2" />
                  )}
                  Create Action Plan
                </button>
              </>
            ) : (
              <button
                onClick={selectAllAccepted}
                className="flex items-center px-4 py-2 border border-legacy-600 text-legacy-600 rounded-md hover:bg-legacy-50 transition-colors"
              >
                <ListTodo className="h-4 w-4 mr-2" />
                Select Accepted ({acceptedRecommendations.length})
              </button>
            )}
          </div>
        )}
      </div>

      {/* Stale analysis banner */}
      {isAnalysisStale && recommendations.some((r) =>
        (r.agentType === 'tax' && isTaxStale) || (r.agentType === 'estate' && isEstateStale)
      ) && (
        <div className="flex items-center gap-2 px-4 py-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800">
          <AlertTriangle className="h-4 w-4 flex-shrink-0" />
          <span>Your profile data has changed since the last analysis. Consider re-running analysis for up-to-date recommendations.</span>
          <button
            onClick={() => navigate('/agents')}
            className="ml-auto text-yellow-900 font-medium hover:underline whitespace-nowrap"
          >
            Re-run Analysis
          </button>
        </div>
      )}

      {recommendations.length > 0 ? (
        <div className="space-y-4">
          {recommendations.map((rec) => (
            <RecommendationCard
              key={rec.id}
              rec={rec}
              isStale={(rec.agentType === 'tax' && isTaxStale) || (rec.agentType === 'estate' && isEstateStale) || (rec.agentType === 'insurance' && isInsuranceStale)}
              isEvidenceExpanded={expandedEvidence.has(rec.id)}
              isSelected={selectedIds.has(rec.id)}
              onToggleSelection={() => toggleSelection(rec.id)}
              onToggleEvidence={() => toggleEvidence(rec.id)}
              onAccept={() => handleAccept(rec)}
              onReject={() => setRejectTarget(rec.id)}
              onReviewLater={() => updateStatusMutation.mutate({ id: rec.id, status: 'viewed' })}
              isPending={updateStatusMutation.isPending}
            />
          ))}
        </div>
      ) : (
        <div className="card">
          <EmptyState
            icon={Lightbulb}
            title="No recommendations yet"
            description="Run an analysis to generate personalized recommendations for your wealth strategy."
            action={{ label: 'Run Analysis', href: '/agents' }}
          />
        </div>
      )}

      <ConfirmDialog
        open={rejectTarget !== null}
        title="Reject recommendation"
        description="Are you sure you want to reject this recommendation? You can still review it later from the list."
        confirmLabel="Reject"
        destructive
        onConfirm={() => {
          if (rejectTarget) updateStatusMutation.mutate({ id: rejectTarget, status: 'rejected' });
          setRejectTarget(null);
        }}
        onCancel={() => setRejectTarget(null)}
      />

      {/* Stale accept warning */}
      <ConfirmDialog
        open={staleAcceptTarget !== null}
        title="Accept outdated recommendation?"
        description="Your financial data has changed since this recommendation was generated. We recommend re-running tax analysis first. Accept anyway?"
        confirmLabel="Accept Anyway"
        onConfirm={() => {
          if (staleAcceptTarget) updateStatusMutation.mutate({ id: staleAcceptTarget, status: 'accepted' });
          setStaleAcceptTarget(null);
        }}
        onCancel={() => setStaleAcceptTarget(null)}
      />
    </div>
  );
}

// =============================================================================
// RecommendationCard — handles evidence expansion and stale badge
// =============================================================================

function RecommendationCard({
  rec,
  isStale,
  isEvidenceExpanded,
  isSelected,
  onToggleSelection,
  onToggleEvidence,
  onAccept,
  onReject,
  onReviewLater,
  isPending,
}: {
  rec: Recommendation;
  isStale: boolean;
  isEvidenceExpanded: boolean;
  isSelected: boolean;
  onToggleSelection: () => void;
  onToggleEvidence: () => void;
  onAccept: () => void;
  onReject: () => void;
  onReviewLater: () => void;
  isPending: boolean;
}) {
  // Fetch detail (with evidence) only when expanded
  const { data: detail } = useQuery({
    queryKey: ['recommendation', rec.id],
    queryFn: () => api.getRecommendation(rec.id),
    enabled: isEvidenceExpanded,
  });

  const hasEvidence = (rec.evidenceIds?.length ?? 0) > 0;

  return (
    <div className={clsx('card', isStale && 'border-yellow-300 bg-yellow-50/30')}>
      <div className="flex items-start">
        {/* Checkbox for accepted recommendations */}
        {rec.status === 'accepted' && (
          <div className="flex-shrink-0 mr-4 pt-1">
            <input
              type="checkbox"
              checked={isSelected}
              onChange={onToggleSelection}
              className="h-4 w-4 text-legacy-600 rounded border-gray-300 focus:ring-legacy-500 cursor-pointer"
            />
          </div>
        )}

        <div className="flex-1">
          <div className="flex items-center space-x-2 mb-2">
            <PriorityBadge priority={rec.priority} />
            <StatusBadge status={rec.status} />
            {rec.requiresProfessionalReview && (
              <span className="px-2 py-1 text-xs font-medium rounded-full bg-purple-100 text-purple-800">
                Professional Review
              </span>
            )}
            {isStale && (
              <span className="px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800">
                Based on outdated data
              </span>
            )}
          </div>
          <h3 className="text-lg font-semibold text-gray-900">{rec.title}</h3>
          <p className="text-gray-600 mt-2">{rec.summary}</p>
          <div className="flex items-center space-x-4 mt-3 text-sm text-gray-500">
            <span className="capitalize">{rec.agentType} Analysis</span>
            <span>{rec.category}</span>
            <span>{Math.round(rec.confidence * 100)}% confidence</span>
            <span>{new Date(rec.createdAt).toLocaleDateString()}</span>
          </div>
        </div>
      </div>

      {/* Evidence toggle */}
      {hasEvidence && (
        <div className="mt-3 pt-3 border-t border-gray-100">
          <button
            onClick={onToggleEvidence}
            className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
          >
            {isEvidenceExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            View evidence
          </button>
          {isEvidenceExpanded && detail?.evidence && (
            <div className="mt-3">
              <EvidenceDetail evidence={detail.evidence} />
            </div>
          )}
          {isEvidenceExpanded && !detail?.evidence && (
            <div className="mt-3 flex items-center gap-2 text-sm text-gray-400">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading evidence...
            </div>
          )}
        </div>
      )}

      {rec.status === 'pending' && (
        <div className="mt-4 pt-4 border-t">
          <p className="text-xs text-gray-400 mb-2">Accept to include in an action plan, or reject to remove from your queue</p>
          <div className="flex items-center space-x-2">
          <button
            onClick={onAccept}
            disabled={isPending}
            className="flex items-center px-3 py-1.5 text-sm font-medium rounded-md bg-green-600 text-white hover:bg-green-700 transition-colors disabled:opacity-50"
          >
            <CheckCircle className="h-4 w-4 mr-1.5" />
            Accept
          </button>
          <button
            onClick={onReject}
            disabled={isPending}
            className="flex items-center px-3 py-1.5 text-sm font-medium rounded-md bg-red-600 text-white hover:bg-red-700 transition-colors disabled:opacity-50"
          >
            <XCircle className="h-4 w-4 mr-1.5" />
            Reject
          </button>
          <button
            onClick={onReviewLater}
            disabled={isPending}
            className="flex items-center px-3 py-1.5 text-sm font-medium rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            <Clock className="h-4 w-4 mr-1.5" />
            Review Later
          </button>
          </div>
        </div>
      )}
    </div>
  );
}

function PriorityBadge({ priority }: { priority: string }) {
  const colors: Record<string, string> = {
    critical: 'bg-red-100 text-red-800',
    high: 'bg-orange-100 text-orange-800',
    medium: 'bg-yellow-100 text-yellow-800',
    low: 'bg-green-100 text-green-800',
  };

  const icons: Record<string, React.ReactNode> = {
    critical: <AlertTriangle className="h-3 w-3 mr-1" />,
    high: <AlertTriangle className="h-3 w-3 mr-1" />,
  };

  return (
    <span className={clsx('flex items-center px-2 py-1 text-xs font-medium rounded-full', colors[priority] ?? colors.medium)}>
      {icons[priority]}
      {priority}
    </span>
  );
}

function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    pending: 'bg-gray-100 text-gray-800',
    viewed: 'bg-blue-100 text-blue-800',
    accepted: 'bg-green-100 text-green-800',
    rejected: 'bg-red-100 text-red-800',
    implemented: 'bg-purple-100 text-purple-800',
    expired: 'bg-gray-100 text-gray-500',
  };

  return (
    <span className={clsx('px-2 py-1 text-xs font-medium rounded-full', colors[status] ?? colors.pending)}>
      {status}
    </span>
  );
}
